import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import { Toast } from 'primereact/toast';


import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import useReceiptPrinter from '../../utils/ReceiptPrint';
import useValidateShift from '../../utils/ShiftUtils';
import { formatCurrency, formatShortDate, pluralize } from '../../utils/CommonUtils';
import { useIsDesktop } from '../../utils/useMediaQuery';
import { signalFailure, signalSuccess } from '../../utils/feedback';

import Card from '../common/Card';
import Drawer from '../common/Drawer';
import EmptyState from '../common/EmptyState';
import Money from '../common/Money';
import PaymentEditor, { UndistributedMessage } from '../common/PaymentEditor';
import SummaryList, { SummaryRow } from '../common/SummaryList';

import BarcodeScanner from './BarcodeScanner';
import CatalogTile from './CatalogTile';
import Cart from './Cart';
import ClientPicker from './ClientPicker';

const CATALOG_PAGE_SIZE = 10;
const SEARCH_MIN_LENGTH = 3;

const EMPTY_SALE = {
    products: [],
    discount: [],
    basePayments: [],
    payments: [],
    client: null,
    isEdit: false,
};

/**
 * Екран продажу: каталог → кошик → оплата.
 *
 * Основні зміни в порівнянні з попередньою версією:
 *  • одне поле пошуку з перемикачем «Товари / Послуги» замість двох полів,
 *    що виглядали однаково і давали різний результат;
 *  • «хлібні крихти» категорій замість плитки «До попередньої категорії»,
 *    яка займала місце товару в сітці;
 *  • сторінки каталогу — окремим блоком під сіткою, а не плитками
 *    «Попередня / Наступна сторінка» всередині неї;
 *  • кошик з кроковим лічильником кількості (раніше — редагування комірки
 *    таблиці, у яке на телефоні складно влучити);
 *  • підсумок і кнопка оплати завжди закріплені внизу.
 */
const NewSale = () => {
    const toast = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    const authenticatedRequest = useAuthenticatedRequest();
    const printReceipt = useReceiptPrinter();
    const validateShift = useValidateShift();

    // ── Каталог ─────────────────────────────────────────────────────────
    const [catalogKind, setCatalogKind] = useState(null); // 'products' | 'services'
    const [catalogItems, setCatalogItems] = useState(null);
    const [catalogPage, setCatalogPage] = useState(1);
    const [catalogPath, setCatalogPath] = useState([]); // [{ id, name }]
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // ── Кошик і продаж ──────────────────────────────────────────────────
    const [cart, setCart] = useState([]);
    const [saleDraft, setSaleDraft] = useState(EMPTY_SALE);
    const [step, setStep] = useState('catalog'); // 'catalog' | 'checkout'
    const [currencyRates, setCurrencyRates] = useState(null);
    const [totalSumUAH, setTotalSumUAH] = useState(0);
    const [busy, setBusy] = useState(false);

    // ── Панелі ──────────────────────────────────────────────────────────
    const [cartVisible, setCartVisible] = useState(false);
    const [scannerVisible, setScannerVisible] = useState(false);
    const [scanBusy, setScanBusy] = useState(false);
    // Саме ref, а не state: кадри з камери йдуть десятки разів на секунду,
    // і поки React перерендерить компонент, встигне пройти ще кілька
    // спрацювань зі старим значенням прапорця.
    const scanLockRef = useRef(false);
    const [clientPickerVisible, setClientPickerVisible] = useState(false);
    const [discountVisible, setDiscountVisible] = useState(false);
    const [paymentVisible, setPaymentVisible] = useState(false);
    const [receiptVisible, setReceiptVisible] = useState(false);
    const [paymentErrors, setPaymentErrors] = useState([]);
    const [actId, setActId] = useState(null);

    // ── Курси валют ─────────────────────────────────────────────────────
    useEffect(() => {
        const fetchRates = async () => {
            const url = `${process.env.REACT_APP_BACKEND_URL}currency/get_currencies_list/`;
            const response = await authenticatedRequest(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (response?.status === 200) {
                const parsed = await response.json();
                setCurrencyRates(
                    parsed.reduce((acc, item) => {
                        acc[item.currency_short] = item.currency_rate;
                        return acc;
                    }, {})
                );
            }
        };

        fetchRates();
    }, [authenticatedRequest]);

    // ── Режим редагування чернетки (перехід із «Незакритих замовлень») ──
    useEffect(() => {
        const sale = location.state?.sale;

        if (!sale) {
            return;
        }

        setCart(
            sale.products.map((item) => {
                const isService = item.service !== undefined;

                return {
                    id: isService ? item.id : item.remaining_id,
                    item_type: isService ? 'SERVICE' : 'PRODUCT',
                    name: isService ? item.service : item.product,
                    quantity: item.quantity ?? 1,
                    price: item.price,
                    currency: item.currency,
                    product_identifier: item.identifier ?? null,
                    remaining_id: item.remaining_id,
                };
            })
        );

        setSaleDraft({
            ...EMPTY_SALE,
            isEdit: true,
            discount: sale.discount ?? [],
            client: sale.cagent ?? null,
            actDate: sale.act_date,
            actNumber: sale.act_number,
            payDueDate: sale.pay_due_date,
            saleId: sale.id,
        });
    }, [location.state]);

    // ── Завантаження каталогу ───────────────────────────────────────────
    const loadCatalog = useCallback(
        async ({ kind, groupId, page = 1, search = '', path }) => {
            const effectiveKind = kind ?? catalogKind;

            if (!effectiveKind) {
                return;
            }

            const trimmed = search.trim();

            // Пошук стартує від трьох символів — інакше бекенд повертає
            // весь довідник і застосунок «підвисає» на першій літері.
            if (trimmed && trimmed.length < SEARCH_MIN_LENGTH) {
                return;
            }

            setCatalogLoading(true);

            const params = new URLSearchParams({
                page: String(page),
                page_size: String(CATALOG_PAGE_SIZE),
            });

            if (groupId !== undefined && groupId !== null) {
                params.set('group_id', String(groupId));
            }

            if (trimmed) {
                // products → product_name, services → service_name
                params.set(`${effectiveKind.slice(0, -1)}_name`, trimmed);
            }

            const url = `${process.env.REACT_APP_BACKEND_URL}kasa_shifts/get_${effectiveKind}_catalog/?${params}`;

            try {
                const response = await authenticatedRequest(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (response?.status !== 200) {
                    throw new Error(`HTTP ${response?.status}`);
                }

                const parsed = await response.json();

                setCatalogItems(parsed.results ?? []);
                setCatalogKind(effectiveKind);
                setCatalogPage(page);

                if (path !== undefined) {
                    setCatalogPath(path);
                }
            } catch (error) {
                console.error('Не вдалося отримати каталог', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Каталог недоступний',
                    detail: 'Перевірте зв’язок із сервером',
                    life: 4000,
                });
            } finally {
                setCatalogLoading(false);
            }
        },
        [authenticatedRequest, catalogKind]
    );

    // Пошук із затримкою: запит іде через 350 мс після останнього символу
    useEffect(() => {
        if (!catalogKind) {
            return undefined;
        }

        const trimmed = searchTerm.trim();

        if (trimmed && trimmed.length < SEARCH_MIN_LENGTH) {
            return undefined;
        }

        const timer = setTimeout(() => {
            loadCatalog({
                kind: catalogKind,
                groupId: trimmed ? undefined : catalogPath[catalogPath.length - 1]?.id,
                page: 1,
                search: trimmed,
                path: trimmed ? [] : undefined,
            });
        }, 350);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const openGroup = (group) => {
        loadCatalog({
            groupId: group.id,
            page: 1,
            path: [...catalogPath, { id: group.id, name: group.name }],
        });
    };

    const goToPathIndex = (index) => {
        const path = catalogPath.slice(0, index + 1);
        loadCatalog({ groupId: path[path.length - 1]?.id, page: 1, path });
    };

    const resetCatalog = () => {
        setCatalogKind(null);
        setCatalogItems(null);
        setCatalogPath([]);
        setCatalogPage(1);
        setSearchTerm('');
    };

    // ── Кошик ───────────────────────────────────────────────────────────
    const addToCart = useCallback((item) => {
        setCart((current) => {
            const index = current.findIndex(
                (line) => line.id === item.id && line.item_type === item.item_type
            );

            // Товари з IMEI унікальні — їх не можна додати двічі
            if (index >= 0 && !current[index].product_identifier) {
                return current.map((line, i) =>
                    i === index ? { ...line, quantity: (line.quantity ?? 1) + 1 } : line
                );
            }

            if (index >= 0) {
                return current;
            }

            return [...current, { ...item, quantity: 1 }];
        });
    }, []);

    const changeQuantity = (index, quantity) => {
        setCart((current) =>
            current.map((line, i) => (i === index ? { ...line, quantity } : line))
        );
    };

    const removeFromCart = (index) => {
        setCart((current) => current.filter((_, i) => i !== index));
    };

    const cartTotals = useMemo(() => {
        const totals = {};

        cart.forEach((item) => {
            const currency = item.currency;
            const quantity = item.quantity ?? 1;

            if (!totals[currency]) {
                totals[currency] = { currency, sum: 0, paymentMethod: null };
            }

            totals[currency].sum += item.price * quantity;
        });

        return Object.values(totals);
    }, [cart]);

    const cartTotalUAH = useMemo(() => {
        if (!currencyRates) {
            return 0;
        }

        return cartTotals.reduce(
            (sum, total) => sum + total.sum * (currencyRates[total.currency] ?? 0),
            0
        );
    }, [cartTotals, currencyRates]);

    // ── Сканер штрих-кодів ──────────────────────────────────────────────
    const handleScan = useCallback(
        async (barcode) => {
            // Поки шукаємо товар на сервері, нові кадри ігноруємо.
            if (scanLockRef.current) {
                return;
            }

            scanLockRef.current = true;
            setScanBusy(true);

            try {
                const url = `${process.env.REACT_APP_BACKEND_URL}remainings/get_remainings/?barcode=${encodeURIComponent(
                    barcode
                )}`;
                const response = await authenticatedRequest(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });

                let product = null;

                if (response?.status === 200) {
                    const parsed = await response.json();
                    product = parsed.results?.[0] ?? null;
                }

                if (product) {
                    addToCart({
                        id: product.id,
                        item_type: 'PRODUCT',
                        name: product.product_name,
                        price: product.sale_price,
                        currency: product.sale_price_currency,
                        product_identifier: product.identifier ?? null,
                    });
                    signalSuccess();
                    setScannerVisible(false);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Товар додано',
                        detail: product.product_name,
                        life: 2500,
                    });
                } else {
                    signalFailure();
                    toast.current?.show({
                        severity: 'warn',
                        summary: 'Товар не знайдено',
                        detail: `Штрих-код ${barcode} відсутній у залишках`,
                        life: 3500,
                    });
                }
            } catch (error) {
                console.error('Помилка пошуку за штрих-кодом', error);
                signalFailure();
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка пошуку',
                    detail: 'Перевірте зв’язок із сервером',
                    life: 3500,
                });
            } finally {
                scanLockRef.current = false;
                setScanBusy(false);
            }
        },
        [authenticatedRequest, addToCart]
    );

    // ── Перехід до оплати ───────────────────────────────────────────────
    const goToCheckout = () => {
        const discounts = cartTotals.map((total) => ({
            currency: total.currency,
            amount:
                saleDraft.discount.find((item) => item.currency === total.currency)?.amount ?? 0,
        }));

        setSaleDraft((current) => ({
            ...current,
            products: cart,
            basePayments: cartTotals,
            payments: cartTotals.map((total) => ({ ...total })),
            discount: discounts,
        }));

        setTotalSumUAH(cartTotalUAH);
        setStep('checkout');
        setCartVisible(false);
    };

    const discountTotalUAH = useMemo(() => {
        if (!currencyRates) {
            return 0;
        }

        return saleDraft.discount.reduce(
            (sum, item) => sum + Number(item.amount) * (currencyRates[item.currency] ?? 0),
            0
        );
    }, [saleDraft.discount, currencyRates]);

    const paidUAH = useMemo(() => {
        if (!currencyRates) {
            return 0;
        }

        return saleDraft.payments.reduce(
            (sum, payment) => sum + Number(payment.sum) * (currencyRates[payment.currency] ?? 0),
            0
        );
    }, [saleDraft.payments, currencyRates]);

    const undistributed = totalSumUAH - discountTotalUAH - paidUAH;

    const hasDiscount = saleDraft.discount.some((item) => Number(item.amount) > 0);

    /** Сума до оплати з урахуванням знижки, у розрізі валют. */
    const payableTotals = useMemo(
        () =>
            saleDraft.basePayments.map((base) => {
                const discount =
                    saleDraft.discount.find((item) => item.currency === base.currency)?.amount ?? 0;

                return { currency: base.currency, sum: base.sum - Number(discount) };
            }),
        [saleDraft.basePayments, saleDraft.discount]
    );

    // ── Запити на бекенд ────────────────────────────────────────────────
    const buildSaleRequest = () => {
        const request = {
            cagent: saleDraft.client?.id ?? null,
            division: Cookies.get('cashAppDivision'),
            act_date: saleDraft.isEdit ? saleDraft.actDate : formatShortDate(new Date()),
            pay_due_date: saleDraft.isEdit ? saleDraft.payDueDate : formatShortDate(new Date()),
            act_number_autogenerate: true,
            shift_id: Cookies.get('ShiftId'),
            discount: saleDraft.discount.filter((item) => Number(item.amount) !== 0),
            unidentified_products: [],
            identified_products: [],
            services: [],
        };

        if (saleDraft.isEdit) {
            request.act_number = saleDraft.actNumber;
        }

        cart.forEach((item) => {
            const rate = currencyRates?.[item.currency] ?? 0;

            if (item.product_identifier) {
                request.identified_products.push({
                    remaining_id: item.id,
                    price: item.price,
                    priceUAH: item.price * rate,
                    currency: item.currency,
                });
            } else if (item.item_type === 'SERVICE') {
                request.services.push({
                    service_id: item.id,
                    price: item.price,
                    priceUAH: item.price * rate,
                    currency: item.currency,
                });
            } else {
                request.unidentified_products.push({
                    remaining_id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    priceUAH: item.price * rate,
                    currency: item.currency,
                });
            }
        });

        return request;
    };

    const persistSale = async (request) => {
        if (saleDraft.isEdit) {
            const response = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}sale_acts/update/${saleDraft.saleId}/`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(request),
                }
            );

            return response?.status === 200 ? saleDraft.saleId : null;
        }

        const response = await authenticatedRequest(
            `${process.env.REACT_APP_BACKEND_URL}sale_acts/create/`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(request),
            }
        );

        if (response?.status === 201) {
            const parsed = await response.json();
            return parsed.id;
        }

        return null;
    };

    const saveDraft = async () => {
        setBusy(true);

        try {
            if (!(await validateShift())) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'error',
                            summary: 'Зміна закрита',
                            detail: 'Збереження неможливе',
                            life: 4000,
                        },
                    },
                });
                return;
            }

            const saleId = await persistSale(buildSaleRequest());

            if (saleId) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'success',
                            summary: saleDraft.isEdit ? 'Чернетку оновлено' : 'Чернетку збережено',
                            detail: `Акт №${saleId}`,
                            life: 3000,
                        },
                    },
                });
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Не вдалося зберегти акт',
                    life: 4000,
                });
            }
        } finally {
            setBusy(false);
        }
    };

    const submitPayment = async () => {
        const errors = saleDraft.payments.map((payment) =>
            payment.paymentMethod ? {} : { paymentMethod: 'Вкажіть спосіб оплати' }
        );

        setPaymentErrors(errors);

        if (!saleDraft.payments.length || errors.some((error) => error.paymentMethod)) {
            return;
        }

        setBusy(true);

        try {
            if (!(await validateShift())) {
                navigate('/shift', {
                    state: {
                        toast: {
                            severity: 'error',
                            summary: 'Зміна закрита',
                            detail: 'Оплата неможлива',
                            life: 4000,
                        },
                    },
                });
                return;
            }

            const saleId = await persistSale(buildSaleRequest());

            if (!saleId) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Не вдалося створити акт продажу',
                    life: 4000,
                });
                return;
            }

            setActId(saleId);

            const finalizeResponse = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}sale_acts/make_finalized/${saleId}/`,
                { method: 'GET', headers: { 'Content-Type': 'application/json' } }
            );

            if (finalizeResponse?.status !== 200) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка фіналізації акту',
                    detail: `Код помилки: ${finalizeResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
                return;
            }

            const paymentRequest = saleDraft.payments.map((payment) => ({
                act_id: saleId,
                date: formatShortDate(Date.now()),
                sum: payment.sum,
                currency: payment.currency,
                type: payment.paymentMethod,
                method: 'INCOME',
                cagent_id: saleDraft.client?.id ?? null,
                shift_id: Cookies.get('ShiftId'),
            }));

            const paymentResponse = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}accounting_payments/create/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(paymentRequest),
                }
            );

            if (paymentResponse?.status === 201) {
                setPaymentVisible(false);
                setReceiptVisible(true);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Помилка створення платежу',
                    detail: `Код помилки: ${paymentResponse?.status ?? 'невідомо'}`,
                    life: 4000,
                });
            }
        } finally {
            setBusy(false);
        }
    };

    const confirmDeleteAct = () => {
        confirmDialog({
            header: 'Видалення акту',
            message: `Видалити акт №${saleDraft.saleId}? Дію не можна скасувати.`,
            icon: 'pi pi-trash',
            acceptLabel: 'Видалити',
            rejectLabel: 'Скасувати',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const response = await authenticatedRequest(
                    `${process.env.REACT_APP_BACKEND_URL}sale_acts/delete/${saleDraft.saleId}/`,
                    { method: 'DELETE', headers: { 'Content-Type': 'application/json' } }
                );

                navigate('/opensales', {
                    state: {
                        toast:
                            response?.status === 204
                                ? {
                                      severity: 'success',
                                      summary: 'Акт видалено',
                                      life: 3000,
                                  }
                                : {
                                      severity: 'error',
                                      summary: 'Не вдалося видалити акт',
                                      detail: `Код помилки: ${response?.status ?? 'невідомо'}`,
                                      life: 4000,
                                  },
                    },
                });
            },
        });
    };

    // ── Рендер: каталог ─────────────────────────────────────────────────
    const cartComponent = (
        <Cart
            items={cart}
            totals={cartTotals}
            totalUAH={cartTotalUAH}
            onChangeQuantity={changeQuantity}
            onRemove={removeFromCart}
            onCheckout={goToCheckout}
        />
    );

    const catalogView = (
        <div className="rc-sale-layout">
            <div>
                {catalogKind === null ? (
                    <div className="rc-picker-grid">
                        <button
                            type="button"
                            className="rc-picker"
                            onClick={() => loadCatalog({ kind: 'products', page: 1, path: [] })}
                        >
                            <span className="rc-picker__icon">
                                <i className="pi pi-box" />
                            </span>
                            <span className="rc-picker__title">Товари</span>
                            <span className="rc-picker__hint">Каталог залишків на складі</span>
                        </button>

                        <button
                            type="button"
                            className="rc-picker"
                            onClick={() => loadCatalog({ kind: 'services', page: 1, path: [] })}
                        >
                            <span className="rc-picker__icon">
                                <i className="pi pi-wrench" />
                            </span>
                            <span className="rc-picker__title">Послуги</span>
                            <span className="rc-picker__hint">Роботи та сервісні позиції</span>
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="rc-breadcrumb">
                            <button
                                type="button"
                                className="rc-breadcrumb__chip"
                                onClick={resetCatalog}
                            >
                                <i className="pi pi-home" />
                                {catalogKind === 'products' ? 'Товари' : 'Послуги'}
                            </button>
                            {catalogPath.map((group, index) => (
                                <button
                                    key={group.id}
                                    type="button"
                                    className="rc-breadcrumb__chip"
                                    onClick={() => goToPathIndex(index)}
                                >
                                    {group.name}
                                </button>
                            ))}
                        </div>

                        {catalogLoading ? (
                            <EmptyState icon="pi pi-spin pi-spinner" text="Завантаження…" />
                        ) : catalogItems?.length ? (
                            <>
                                <div className="rc-catalog-grid mt-3">
                                    {catalogItems.map((item) => (
                                        <CatalogTile
                                            key={`${item.item_type}-${item.id}`}
                                            item={item}
                                            onOpenGroup={openGroup}
                                            onAdd={(element) =>
                                                addToCart({
                                                    id: element.id,
                                                    item_type:
                                                        element.item_type === 'SERVICE'
                                                            ? 'SERVICE'
                                                            : 'PRODUCT',
                                                    name: element.name,
                                                    price: element.price,
                                                    currency: element.currency ?? 'UAH',
                                                    product_identifier:
                                                        element.product_identifier ?? null,
                                                })
                                            }
                                        />
                                    ))}
                                </div>

                                <div className="rc-catalog-pager">
                                    <Button
                                        type="button"
                                        label="Попередня"
                                        icon="pi pi-chevron-left"
                                        text
                                        disabled={catalogPage <= 1}
                                        onClick={() =>
                                            loadCatalog({
                                                groupId: catalogPath[catalogPath.length - 1]?.id,
                                                page: catalogPage - 1,
                                                search: searchTerm,
                                            })
                                        }
                                    />
                                    <span className="rc-page-subtitle">
                                        Сторінка {catalogPage}
                                    </span>
                                    <Button
                                        type="button"
                                        label="Наступна"
                                        icon="pi pi-chevron-right"
                                        iconPos="right"
                                        text
                                        disabled={catalogItems.length < CATALOG_PAGE_SIZE}
                                        onClick={() =>
                                            loadCatalog({
                                                groupId: catalogPath[catalogPath.length - 1]?.id,
                                                page: catalogPage + 1,
                                                search: searchTerm,
                                            })
                                        }
                                    />
                                </div>
                            </>
                        ) : (
                            <EmptyState
                                icon="pi pi-search"
                                title="Нічого не знайдено"
                                text={
                                    searchTerm
                                        ? `За запитом «${searchTerm}» немає позицій`
                                        : 'У цій категорії поки що порожньо'
                                }
                            />
                        )}
                    </>
                )}
            </div>

            <div className="rc-cart-column">
                <Card title="Замовлення" flush>
                    {cartComponent}
                </Card>
            </div>
        </div>
    );

    // ── Рендер: оплата ──────────────────────────────────────────────────
    // Підсумок і кнопки оплати не розтягуємо на всю ширину екрана
    // продажу — колонка сум читається краще вузькою й по центру
    const checkoutView = (
        <div className="rc-stack rc-narrow">
            <div>
                <h1 className="rc-page-title">
                    {saleDraft.isEdit ? `Акт №${saleDraft.saleId}` : 'До оплати'}
                </h1>
                <p className="rc-page-subtitle">
                    {pluralize(cart.length, ['позиція', 'позиції', 'позицій'])} ·{' '}
                    {saleDraft.client?.name ?? 'Клієнта не вказано'}
                </p>
            </div>

            {/* Перелік позицій: на кроці оплати касир має бачити, за що
                бере гроші, не повертаючись назад до каталогу. Список
                прокручується всередині картки, щоб кнопка оплати
                не їхала за межі екрана на великому замовленні. */}
            <Card
                title={`Товари та послуги · ${pluralize(cart.length, [
                    'позиція',
                    'позиції',
                    'позицій',
                ])}`}
                flush
            >
                <div className="rc-checkout-items">
                    {cart.map((item, index) => {
                        const quantity = item.quantity ?? 1;

                        return (
                            <div
                                className="rc-line-item"
                                // eslint-disable-next-line react/no-array-index-key
                                key={`checkout-${item.item_type}-${item.id}-${index}`}
                            >
                                <div className="rc-line-item__main">
                                    <div className="rc-line-item__name">{item.name}</div>
                                    <div className="rc-line-item__meta">
                                        <span>
                                            {quantity} × {formatCurrency(item.price, item.currency)}
                                        </span>
                                    </div>
                                    {/* IMEI окремим рядком: разом із ціною він
                                        переносився, і крапка-роздільник лишалася
                                        висіти на початку другого рядка */}
                                    {item.product_identifier && (
                                        <div className="rc-line-item__meta">
                                            <span>IMEI {item.product_identifier}</span>
                                        </div>
                                    )}
                                </div>
                                <Money value={item.price * quantity} currency={item.currency} />
                            </div>
                        );
                    })}
                </div>
            </Card>

            <Card title="Сума замовлення" flush>
                <SummaryList>
                    {saleDraft.basePayments.map((payment) => (
                        <SummaryRow key={`base-${payment.currency}`} label={payment.currency}>
                            <Money value={payment.sum} currency={payment.currency} />
                        </SummaryRow>
                    ))}
                    {hasDiscount &&
                        saleDraft.discount
                            .filter((item) => Number(item.amount) > 0)
                            .map((item) => (
                                <SummaryRow
                                    key={`discount-${item.currency}`}
                                    label={`Знижка, ${item.currency}`}
                                >
                                    <Money
                                        value={-item.amount}
                                        currency={item.currency}
                                        tone="outcome"
                                    />
                                </SummaryRow>
                            ))}
                    <SummaryRow label="До сплати" sublabel="еквівалент у гривні" total>
                        <Money value={totalSumUAH - discountTotalUAH} currency="UAH" />
                    </SummaryRow>
                </SummaryList>
            </Card>

            <div className="rc-stack rc-stack--tight">
                <Button
                    label={saleDraft.client ? `Клієнт: ${saleDraft.client.name}` : 'Обрати клієнта'}
                    icon="pi pi-user"
                    severity="secondary"
                    outlined
                    className="w-full"
                    onClick={() => setClientPickerVisible(true)}
                />
                <Button
                    label={hasDiscount ? 'Змінити знижку' : 'Додати знижку'}
                    icon="pi pi-percentage"
                    severity="secondary"
                    outlined
                    className="w-full"
                    onClick={() => setDiscountVisible(true)}
                />
            </div>

            <div className="rc-stack rc-stack--tight">
                <Button
                    label="Оплатити"
                    icon="pi pi-credit-card"
                    className="w-full"
                    onClick={() => {
                        setPaymentErrors([]);
                        setSaleDraft((current) => ({
                            ...current,
                            payments: payableTotals.map((total) => ({
                                ...total,
                                paymentMethod:
                                    current.payments.find(
                                        (payment) => payment.currency === total.currency
                                    )?.paymentMethod ?? null,
                            })),
                        }));
                        setPaymentVisible(true);
                    }}
                />
                <Button
                    label="Зберегти і вийти"
                    icon="pi pi-save"
                    severity="secondary"
                    outlined
                    className="w-full"
                    loading={busy}
                    onClick={saveDraft}
                />
                <div className="rc-action-grid rc-action-grid--split">
                    <Button
                        label="Назад до товарів"
                        icon="pi pi-arrow-left"
                        text
                        onClick={() => setStep('catalog')}
                    />
                    {saleDraft.isEdit && (
                        <Button
                            label="Видалити акт"
                            icon="pi pi-trash"
                            severity="danger"
                            text
                            onClick={confirmDeleteAct}
                        />
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-center" />

            {step === 'catalog' && (
                <div className="rc-catalog-toolbar">
                    <div className="rc-catalog-toolbar__kind">
                        <SelectButton
                            value={catalogKind}
                            options={[
                                { label: 'Товари', value: 'products' },
                                { label: 'Послуги', value: 'services' },
                            ]}
                            onChange={(e) => {
                                if (e.value) {
                                    setSearchTerm('');
                                    loadCatalog({ kind: e.value, page: 1, path: [] });
                                }
                            }}
                            allowEmpty={false}
                        />
                    </div>
                    {/* Плейсхолдер короткий навмисно: що саме шукаємо, видно
                        на перемикачі вище, а довгий текст обрізався на телефоні
                        поруч із кнопкою кошика */}
                    <IconField iconPosition="left" className="rc-catalog-toolbar__search">
                        <InputIcon className="pi pi-search" />
                        <InputText
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Пошук"
                            disabled={!catalogKind}
                            aria-label={
                                catalogKind === 'services'
                                    ? 'Пошук послуги в каталозі'
                                    : 'Пошук товару в каталозі'
                            }
                        />
                    </IconField>
                    <Button
                        icon="pi pi-barcode"
                        severity="secondary"
                        outlined
                        aria-label="Сканувати штрих-код"
                        onClick={() => setScannerVisible(true)}
                    />
                    {/* Підпис і лічильник, а не сама іконка: з бейджем-дитиною
                        кнопка не вміщала власний вміст і обрізала його */}
                    <Button
                        icon="pi pi-shopping-cart"
                        label="Кошик"
                        badge={cart.length > 0 ? String(cart.length) : null}
                        badgeClassName="p-badge-danger"
                        severity="secondary"
                        outlined
                        className="lg:hidden"
                        aria-label={`Відкрити кошик, позицій: ${cart.length}`}
                        onClick={() => setCartVisible(true)}
                    />
                </div>
            )}

            {step === 'catalog' ? catalogView : checkoutView}

            {/* Кошик (телефон / планшет) */}
            <Drawer
                visible={cartVisible && !isDesktop}
                onHide={() => setCartVisible(false)}
                title="Замовлення"
                flush
            >
                {cartComponent}
            </Drawer>

            {/* Сканер */}
            <BarcodeScanner
                visible={scannerVisible}
                onHide={() => setScannerVisible(false)}
                onDetect={handleScan}
                busy={scanBusy}
            />

            {/* Вибір клієнта */}
            <ClientPicker
                visible={clientPickerVisible}
                onHide={() => setClientPickerVisible(false)}
                selected={saleDraft.client}
                onSelect={(client) => {
                    setSaleDraft((current) => ({ ...current, client }));
                    setClientPickerVisible(false);
                }}
            />

            {/* Знижка */}
            <Drawer
                visible={discountVisible}
                onHide={() => setDiscountVisible(false)}
                title="Знижка"
                footer={
                    <Button
                        label="Застосувати"
                        icon="pi pi-check"
                        className="w-full"
                        onClick={() => setDiscountVisible(false)}
                    />
                }
            >
                <Message
                    className="rc-message"
                    severity="info"
                    text="Знижка вказується в абсолютній сумі для кожної валюти замовлення"
                />
                {saleDraft.discount.map((item, index) => (
                    <div className="rc-payment-row" key={`discount-row-${item.currency}`}>
                        <div className="rc-payment-row__top">
                            <div className="rc-payment-row__currency">
                                <InputText value={item.currency} disabled />
                            </div>
                            <div className="rc-payment-row__amount">
                                <InputNumber
                                    value={Number(item.amount) || 0}
                                    onValueChange={(e) =>
                                        setSaleDraft((current) => ({
                                            ...current,
                                            discount: current.discount.map((discount, i) =>
                                                i === index
                                                    ? { ...discount, amount: e.value ?? 0 }
                                                    : discount
                                            ),
                                        }))
                                    }
                                    mode="decimal"
                                    locale="uk-UA"
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    inputMode="decimal"
                                    max={
                                        saleDraft.basePayments.find(
                                            (base) => base.currency === item.currency
                                        )?.sum
                                    }
                                />
                            </div>
                        </div>
                        <small className="rc-field-hint">
                            Максимум:{' '}
                            {formatCurrency(
                                saleDraft.basePayments.find(
                                    (base) => base.currency === item.currency
                                )?.sum ?? 0,
                                item.currency
                            )}
                        </small>
                    </div>
                ))}
            </Drawer>

            {/* Оплата */}
            <Drawer
                visible={paymentVisible}
                onHide={() => setPaymentVisible(false)}
                title="Оплата"
                footer={
                    <Button
                        label="Провести оплату"
                        icon="pi pi-check"
                        className="w-full"
                        loading={busy}
                        disabled={!saleDraft.payments.length}
                        onClick={submitPayment}
                    />
                }
            >
                <UndistributedMessage amount={undistributed} />
                <PaymentEditor
                    payments={saleDraft.payments}
                    errors={paymentErrors}
                    onChange={(payments) =>
                        setSaleDraft((current) => ({ ...current, payments }))
                    }
                />
            </Drawer>

            {/* Чек після успішної оплати */}
            <Drawer
                visible={receiptVisible}
                onHide={() => navigate('/shift')}
                title="Оплату проведено"
                footer={
                    <>
                        <Button
                            label="Друкувати чек"
                            icon="pi pi-print"
                            className="w-full"
                            onClick={() => printReceipt(actId, toast)}
                        />
                        <Button
                            label="Завершити"
                            severity="secondary"
                            outlined
                            className="w-full"
                            onClick={() => navigate('/shift')}
                        />
                    </>
                }
            >
                <EmptyState
                    icon="pi pi-check-circle"
                    title="Оплату проведено успішно"
                    text={`Акт №${actId} закрито. Можна роздрукувати чек або перейти до наступного продажу.`}
                />
            </Drawer>
        </>
    );
};

export default NewSale;
